/**
 * Utility functions for glossary management
 */

/**
 * Extract display name from glossary object with fallback logic
 * @param {Object} glossary - Glossary object (tenant or shared)
 * @returns {string} Display name or fallback value
 */
export const getGlossaryDisplayName = (glossary) => {
  if (!glossary) {
    return '';
  }

  // Return displayName if available
  if (glossary.displayName) {
    return glossary.displayName;
  }

  // Fallback 1: Use tenantGlossaryName if it's NOT a GUID (legacy data)
  if (glossary.tenantGlossaryName && !isGUID(glossary.tenantGlossaryName)) {
    return glossary.tenantGlossaryName;
  }

  // Fallback 2: Use sharedGlossaryName for shared glossaries
  if (glossary.sharedGlossaryName || glossary.categoryKey) {
    return glossary.sharedGlossaryName || glossary.categoryKey;
  }

  // Fallback 3: Use description or return "Unnamed Glossary"
  return glossary.description || 'Unnamed Glossary';
};

/**
 * Validate display name format
 * @param {string} displayName - Display name to validate
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateDisplayName = (displayName) => {
  if (!displayName || displayName.trim() === '') {
    return {
      valid: false,
      error: 'DisplayName is required'
    };
  }

  const trimmed = displayName.trim();

  if (trimmed.length > 128) {
    return {
      valid: false,
      error: 'DisplayName must not exceed 128 characters'
    };
  }

  // DisplayName allows any Unicode characters (spaces, emojis, special chars)
  // No additional validation needed beyond length
  return {
    valid: true,
    error: null
  };
};

/**
 * Validate shared glossary internal name format
 * @param {string} name - Internal glossary name to validate
 * @returns {Object} { valid: boolean, error: string|null }
 */
export const validateSharedGlossaryName = (name) => {
  if (!name || name.trim() === '') {
    return {
      valid: false,
      error: 'Shared glossary name is required'
    };
  }

  const trimmed = name.trim();

  if (trimmed.length > 50) {
    return {
      valid: false,
      error: 'Shared glossary name must not exceed 50 characters'
    };
  }

  // Shared glossary names: lowercase alphanumeric + hyphens only
  // Examples: "medical-cardiology", "legal-corporate-mergers"
  const regex = /^[a-z0-9]+(-[a-z0-9]+)*$/;

  if (!regex.test(trimmed)) {
    return {
      valid: false,
      error: 'Shared glossary name must be lowercase alphanumeric with hyphens (e.g., "medical-cardiology")'
    };
  }

  return {
    valid: true,
    error: null
  };
};

/**
 * Check if a string is a GUID pattern
 * @param {string} str - String to check
 * @returns {boolean} True if string matches GUID pattern
 */
export const isGUID = (str) => {
  if (!str || typeof str !== 'string') {
    return false;
  }

  // GUID pattern: 8-4-4-4-12 hexadecimal characters
  // Example: 550e8400-e29b-41d4-a716-446655440000
  const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  return guidRegex.test(str);
};

/**
 * Get glossary internal identifier (GUID for tenant, name for shared)
 * @param {Object} glossary - Glossary object
 * @returns {string} Internal identifier
 */
export const getGlossaryId = (glossary) => {
  if (!glossary) {
    return '';
  }

  // Tenant glossary: use tenantGlossaryName (GUID)
  if (glossary.tenantGlossaryName) {
    return glossary.tenantGlossaryName;
  }

  // Shared glossary: use sharedGlossaryName or categoryKey
  return glossary.sharedGlossaryName || glossary.categoryKey || '';
};

/**
 * Check if glossary is a tenant-specific glossary
 * @param {Object} glossary - Glossary object
 * @returns {boolean} True if tenant glossary
 */
export const isTenantGlossary = (glossary) => {
  if (!glossary) {
    return false;
  }

  return Boolean(glossary.tenantGlossaryName || glossary.tenantId);
};

/**
 * Check if glossary is a shared/category glossary
 * @param {Object} glossary - Glossary object
 * @returns {boolean} True if shared glossary
 */
export const isSharedGlossary = (glossary) => {
  if (!glossary) {
    return false;
  }

  return Boolean(glossary.sharedGlossaryName || glossary.categoryKey);
};

/**
 * Format glossary for display in dropdown
 * @param {Object} glossary - Glossary object
 * @returns {Object} { value: string, label: string }
 */
export const formatGlossaryForDropdown = (glossary) => {
  return {
    value: getGlossaryId(glossary),
    label: getGlossaryDisplayName(glossary)
  };
};

/**
 * Flatten V2 glossary entries (grouped by source term) into flat CRUD format (one row per source-target pair).
 * The generate API returns V2 format with translations[] arrays; the CRUD save API expects flat entries.
 * @param {Array} v2Entries - Array of V2 entries with sourceText and translations[]
 * @returns {Array} Flat array of entries with sourceLanguage, targetLanguage, sourceText, targetText
 */
export const flattenV2Entries = (v2Entries) => {
    const flat = [];
    for (const entry of v2Entries) {
        for (const t of (entry.translations || [])) {
            if (!t.targetText || !t.targetLanguage) continue;
            flat.push({
                sourceLanguage: entry.sourceLanguage || 'en',
                targetLanguage: t.targetLanguage,
                sourceText: entry.sourceText,
                targetText: t.targetText,
                caseSensitive: false,
                priority: 0
            });
        }
    }
    return flat;
};
